<template>
  <Box class="notifications-container">
    <div class="search-bar" data-testid="search-bar">
      <Search placeholder="Buscar notificações" :disabled="loading" @submit:search="submitSearch" class="search"
        data-testid="search-input" />

      <div class="actions" data-testid="actions-container">
        <button v-if="showReadButton" title="Marcar como 'Lido'." @click="readNotifications"
          data-testid="mark-read-btn">
          <EmailCheck :size="18" />
        </button>

        <button :title="isArchivedTab ? 'Desarquivar' : 'Arquivar'" @click="changeArchived" data-testid="archive-btn">
          <component :is="isArchivedTab ? 'PackageUp' : 'PackageDown'" :size="18" />
        </button>

        <button v-if="idRole == 2" title="Deletar" @click="deleteNotifications" data-testid="delete-btn">
          <DeleteOutline :size="18" />
        </button>
      </div>
    </div>

    <div class="d-flex justify-content-center" v-if="!loaded" data-testid="loading-container">
      <LoadingSpinner data-testid="loading-spinner" />
    </div>

    <div v-else class="notification-list" data-testid="notification-list">
      <Paginate :total-pages="paginationData.lastPage" :current-page="paginationData.currentPage"
        @page-changed="fetchNotifications" class="paginate" data-testid="pagination" />

      <div v-if="!notifications.length" class="empty" data-testid="empty-state">
        Sem notificações
      </div>

      <div v-for="(notification) in notifications" :key="notification.id" class="notification-item"
        :class="{ 'read': notification.is_read, 'highlighted': isHighlighted(notification.id) }"
        :id="`notification-${notification.id}`" :data-testid="`notification-item-${notification.id}`">
        <input type="checkbox" v-model="notification.selected"
          :data-testid="`notification-checkbox-${notification.id}`" />

        <div class="notification-item-content" :data-testid="`notification-content-${notification.id}`">
          <a v-if="notification.link" :href="notification.link" class="item-title" :title="notification.link"
            :data-testid="`notification-title-${notification.id}`">{{
              notification.title }}</a>
          <strong v-else class="item-title" :data-testid="`notification-title-${notification.id}`">{{ notification.title
          }}</strong>
          <p v-html="notification.description" :data-testid="`notification-description-${notification.id}`"></p>

          <div class="attachments" :data-testid="`notification-attachments-${notification.id}`">
            <div v-for="(attachment, attIndex) in notification.anexos" :key="attIndex" class="attachment-item"
              @click="exportAttchment(attachment)" :data-testid="`attachment-${notification.id}-${attIndex}`">
              <File :size="15" />
              <span class="attachment-name">{{
                attachment.filename.split("-")[1]
              }}</span>
            </div>
          </div>

          <span class="time" :data-testid="`notification-time-${notification.id}`">{{
            utils.formatDate(notification.created_at) }}</span>
        </div>

        <span v-if="!notification.is_read" class="dot" :data-testid="`notification-dot-${notification.id}`"></span>
      </div>
    </div>
  </Box>
</template>

<script>
import Box from "core/Box"
import LoadingSpinner from "core/LoadingSpinner"
import Search from "core/Search"
import Paginate from "core/Paginate"
import PackageDown from "icons/PackageDown"
import PackageUp from "icons/PackageUp"
import EmailCheck from "icons/EmailCheck"
import DeleteOutline from "icons/DeleteOutline"
import File from "icons/File"
import utils from "../../services/utils"

export default {
  name: "NotificationsList",
  components: {
    Box,
    LoadingSpinner,
    Search,
    Paginate,
    PackageDown,
    PackageUp,
    EmailCheck,
    DeleteOutline,
    File,
  },
  props: {
    status: {
      type: String,
      default: null,
    },
    type: {
      type: String,
      default: null,
    },
    showReadButton: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      utils,
      idRole: localStorage.id_role,
      search: "",
      paginationData: {
        currentPage: 1,
        lastPage: 0,
        total: 0,
      },
      notifications: [],
      loaded: false,
      loading: false,
    };
  },
  computed: {
    isArchivedTab() {
      return this.status === "archived"
    },
  },
  watch: {
    '$route.query.highlightId'(newHighlightId, oldHighlightId) {
      if (newHighlightId && newHighlightId !== oldHighlightId && this.loaded) {
        this.$nextTick(() => {
          this.scrollToHighlightedNotification()
        })
      }
    }
  },
  async created() {
    await this.fetchNotifications();

    this.loaded = true
  },
  mounted() {
    this.$nextTick(() => {
      if (this.loaded) {
        this.scrollToHighlightedNotification()
      } else {
        const unwatch = this.$watch('loaded', (newVal) => {
          if (newVal) {
            this.$nextTick(() => {
              this.scrollToHighlightedNotification()
            })
            unwatch()
          }
        })
      }
    })
  },
  methods: {
    async fetchNotifications(page = 1) {
      this.loading = true

      try {
        const paginationData = await this.$store.dispatch('fetchFilteredNotificacoes', {
          http: this.$http,
          page,
          status: this.status,
          type: this.type,
          search: this.search
        })

        this.paginationData = paginationData

        if (!this.status && !this.type) {
          this.notifications = this.$store.getters['notificacoesTodas'].map(item => ({
            ...item,
            selected: false,
          }))
        } else if (this.status === 'unread') {
          this.notifications = this.$store.getters['notificacoesNaoLidas'].map(item => ({
            ...item,
            selected: false,
          }))
        } else if (this.status === 'read') {
          this.notifications = this.$store.getters['notificacoesLidas'].map(item => ({
            ...item,
            selected: false,
          }))
        } else if (this.status === 'archived') {
          this.notifications = this.$store.getters['notificacoesArquivadas'].map(item => ({
            ...item,
            selected: false,
          }))
        } else if (this.type === 'version_control') {
          this.notifications = this.$store.getters['changelogs'].map(item => ({
            ...item,
            selected: false,
          }))
        } else {
          this.notifications = this.$store.getters['notificacoes'].map(item => ({
            ...item,
            selected: false,
          }))
        }
      } catch (err) {
        this.$toasted.show('Erro ao carregar notificações.', { position: 'top-right' })
      }

      this.loading = false
    },
    async readNotifications() {
      const selecteds = this.notifications.filter(item => item.selected)

      if (!selecteds.length) {
        this.$toasted.show('Selecione alguma notificação.', { position: 'top-right' })

        return
      }

      const ids = selecteds.map(item => item.id)

      try {
        const message = await this.$store.dispatch('readNotifications', { http: this.$http, ids })

        this.$toasted.show(message, { position: 'top-right' })

        await this.fetchNotifications(this.paginationData.currentPage)
      } catch (error) {
        this.$toasted.show(error, { position: 'top-right' })
      }
    },
    async changeArchived() {
      const selecteds = this.notifications.filter(item => item.selected)

      if (!selecteds.length) {
        this.$toasted.show('Selecione alguma notificação.', { position: 'top-right' })

        return
      }

      if (selecteds.length > 1) {
        this.$toasted.show(
          `Somente uma notificação pode ser ${this.isArchivedTab ? 'desarquivada' : 'arquivada'} por vez.`,
          { position: 'top-right' }
        )

        return
      }

      try {
        const message = await this.$store.dispatch('changeArchived', {
          http: this.$http,
          id: selecteds[0].id,
          isArchived: this.isArchivedTab
        })

        this.$toasted.show(message, { position: 'top-right' })

        await this.fetchNotifications(this.paginationData.currentPage)
      } catch (error) {
        this.$toasted.show(error, { position: 'top-right' })
      }
    },
    async deleteNotifications() {
      const selecteds = this.notifications.filter(item => item.selected)

      if (!selecteds.length) {
        this.$toasted.show('Selecione alguma notificação.', { position: 'top-right' })

        return
      }

      const ids = selecteds.map(item => item.id)

      try {
        const message = await this.$store.dispatch('deleteNotifications', { http: this.$http, ids })

        this.$toasted.show(message, { position: 'top-right' })

        await this.fetchNotifications(this.paginationData.currentPage)
      } catch (error) {
        this.$toasted.show(error, { position: 'top-right' })
      }
    },
    async exportAttchment(attachment) {
      const base64 = attachment.file

      if (!base64) {
				this.$toasted.show('Anexo não encontrado.', {
					icon: 'fa-times',
					position: 'top-right',
					duration: 5000,
					fullWidth: false,
      	})

				return
			}

      const filename = attachment.filename.split("-")[1]
      const mimetype = attachment.mimetype

      const byteCharacters = atob(base64)
      const byteNumbers = Array.from(byteCharacters, (char) =>
        char.charCodeAt(0)
      )
      const byteArray = new Uint8Array(byteNumbers)
      const blob = new Blob([byteArray], { type: mimetype })

      const link = document.createElement("a")
      link.href = URL.createObjectURL(blob)
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(link.href)
    },
    async submitSearch(e) {
      this.search = e
      await this.fetchNotifications()
    },
    isHighlighted(notificationId) {
      return this.$route.query.highlightId && this.$route.query.highlightId == notificationId
    },
    scrollToHighlightedNotification(retryCount = 0) {
      if (this.$route.query.highlightId) {
        this.$nextTick(() => {
          const element = document.getElementById(`notification-${this.$route.query.highlightId}`)

          if (element) {
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            })

            setTimeout(() => {
              const newQuery = { ...this.$route.query };
              delete newQuery.highlightId;
              this.$router.replace({
                name: this.$route.name,
                query: newQuery
              });
            }, 3000)
          } else if (retryCount < 3) {
            setTimeout(() => {
              this.scrollToHighlightedNotification(retryCount + 1)
            }, 500)
          }
        })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.notifications-container {
  display: flex;
  flex-direction: column;
  gap: 2rem;

  .search-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;

    .search {
      flex: 1;
      max-width: 21.25rem;
    }

    .actions {
      display: flex;

      button {
        background-color: #fafbfd;
        width: 2.5rem;
        height: 2.5rem;
        border: 1px solid #e0e0e0;

        &:first-child {
          border-radius: 6px 0 0 6px;
        }

        &:last-child {
          border-radius: 0 6px 6px 0;
        }

        &:hover {
          background-color: #e0e0e0;
        }
      }
    }
  }

  .notification-list {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    .empty {
      display: flex;
      justify-content: center;
      align-items: center;

      font-size: 1.25rem;
      font-weight: 700;
      color: #202224;
    }

    .paginate {
      align-self: flex-end;
    }

    .notification-item {
      padding: 1rem;
      border: 1px solid #ffc20e;
      border-radius: 14px;

      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 1.25rem;

      &.read {
        border-color: #e0e0e098;
      }

      &.highlighted {
        background-color: #fff3cd;
        border-color: #ffc107;
        box-shadow: 0 0 10px rgba(255, 193, 7, 0.3);
        animation: highlight-pulse 2s ease-in-out;
      }

      .dot {
        display: inline-block;
        width: 8px;
        height: 8px;
        background-color: #ffc20e;
        border-radius: 50%;
      }

      input {
        width: 1rem;
        height: 1rem;
        cursor: pointer;
      }

      .notification-item-content {
        flex: 1;
        min-width: 0; /* permite que o conteúdo encolha dentro do flex container */
        display: flex;
        flex-direction: column;
        gap: 0.5rem;

        .item-title {
          margin: 0;
          font-size: 1rem;
          font-weight: 900;
          color: #1C2D5A;
          display: block;
          word-break: break-word;
          overflow-wrap: anywhere;
        }

        p {
          margin: 0;
          font-size: 0.75rem;
          font-weight: 500;
          color: #202224;
          word-wrap: break-word;
          word-break: break-word;
          overflow-wrap: break-word;
        }

        span {
          font-size: 0.65rem;
          font-weight: 600;
          color: #202224;
        }
      }

      .attachments {
        display: flex;
        flex-direction: column;
        flex-wrap: wrap;
        gap: 0.25rem;

        .attachment-item {
          background-color: #3269ff28;
          border-radius: 6px;
          padding: 0.25rem 0.5rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.875rem;
          color: #202224;
          cursor: pointer;

          &:hover {
            background-color: #3269ff49;
          }
        }
      }
    }
  }
}

@keyframes highlight-pulse {
  0% {
    box-shadow: 0 0 10px rgba(255, 193, 7, 0.3);
  }

  50% {
    box-shadow: 0 0 20px rgba(255, 193, 7, 0.6);
  }

  100% {
    box-shadow: 0 0 10px rgba(255, 193, 7, 0.3);
  }
}
</style>